import { Injectable, OnDestroy } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import { liveQuery } from "dexie";

import * as firebase from "firebase/app";
import "firebase/firestore";
import * as _ from "lodash";
import * as moment from "moment";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { indexedDb } from "../@core/services/indexedDB.service";
import { UserService } from "../@core/services/user.service";
import { convertTimestamp } from "../@core/utils/convertTimestamps.service";
import { Customer } from "../models/customer.model";

@Injectable({
  providedIn: "root",
})
export class CustomerService implements OnDestroy {
  visibility = environment.visibility;
  userLogin: User;
  // shopId: string = localStorage.getItem("shopId")
  //   ? localStorage.getItem("shopId")
  //   : "";
  shopId: string = "";
  private dbPath = "/shops";
  customers$ = liveQuery(() => indexedDb.customers.toArray());
  customers: Customer[] = [];
  destroy$: Subject<void> = new Subject<void>();
  collRef: AngularFirestoreCollection<Customer>;

  private _isLoaded = new BehaviorSubject(false);
  $isLoaded: Observable<boolean> = this._isLoaded.asObservable();

  private _dataChanged = new BehaviorSubject(null);
  $dataChanged: Observable<Customer> = this._dataChanged.asObservable();

  constructor(
    private db: AngularFirestore,
    private userStore: UserService,
    private fns: AngularFireFunctions,
  ) {
    this.userStore
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user) {
          this.userLogin = user;
          this.visibility = user.companyId
            ? user.companyId
            : environment.visibility;

          if (this.shopId !== user.shopId) {
            this.shopId = user.shopId;
            indexedDb.customers
              .toArray()
              .then((data) => {
                if (data.length > 0 && this.shopId !== "") {
                  data = _.filter(data, { shopId: this.shopId });
                } else {
                  data = data ? data : [];
                }
                this.customers = _.reverse(_.sortBy(data, ["updatedOn"]));
                this._isLoaded.next(true);
              })
              .catch((error) => {
                console.log("CustomerService/indexedDb.customers:", error);
                this._isLoaded.next(true);
              });
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initIndexedDbCustomers(shopId: string, isInit?: boolean) {
    let sub: any;
    const lastUpdated = localStorage.getItem(
      `${shopId.toLowerCase()}_customers_updatedOn`,
    );
    isInit = !lastUpdated || isInit ? true : false;

    if (localStorage.getItem("shopId") !== shopId || isInit) {
      sub = this.getCustomers(shopId);
    } else {
      const fromDate =
        lastUpdated && moment(lastUpdated)
          ? moment(lastUpdated, "YYYY-MM-DD HH:mm").add(-1, "days").toDate()
          : moment().add(-5, "days").startOf("day").toDate();

      sub = this.getCustomers(shopId, fromDate);
    }

    sub.then((querySnapshot) => {
      if (querySnapshot.docs.length > 0) {
        querySnapshot.forEach((doc) => {
          let data = new Customer();
          data = doc.data();
          data.id = doc.id;
          data.createdOn = convertTimestamp(doc.data().createdOn);
          data.updatedOn = convertTimestamp(doc.data().updatedOn);
          data.dob = convertTimestamp(doc.data().dob);
          data.cardIdDate = convertTimestamp(doc.data().cardIdDate);

          this.updateIndexedDb(data);
        });
      }
      this._isLoaded.next(true);
      return this.getCustomersChange(shopId);
    });
  }

  private getCustomersChange(shopId: string) {
    this.db
      .collection<Customer>(this.dbPath + `/${shopId}/customers`, (ref) =>
        ref
          // .where("isDeleted", "==", false)
          // .where("isActive", "==", true)
          .where("shopId", "==", shopId)
          .orderBy("updatedOn", "desc")
          .limit(10),
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            id: c.payload.doc.id,
            ...c.payload.doc.data(),
            createdOn: convertTimestamp(c.payload.doc.data().createdOn),
            updatedOn: convertTimestamp(c.payload.doc.data().updatedOn),
            dob: convertTimestamp(c.payload.doc.data().dob),
            cardIdDate: convertTimestamp(c.payload.doc.data().cardIdDate),
          })),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((dataChange) => {
        if (dataChange && dataChange.length > 0) {
          _.forEach(dataChange, (doc) => {
            let prod = new Customer();
            prod = doc;
            prod.id = doc.id;
            prod.createdOn = convertTimestamp(prod.createdOn);
            prod.updatedOn = convertTimestamp(prod.updatedOn);

            if (prod.isDeleted === true) {
              this.deleteIndexedDb(prod.id);
            } else {
              this.updateIndexedDb(prod);
            }
          });

          localStorage.setItem(
            `${shopId.toLowerCase()}_customers_updatedOn`,
            moment().format("YYYY-MM-DD HH:mm"),
          );
        }
      });
  }

  getAllCustomers(shopId: string): AngularFirestoreCollection<Customer> {
    const salesman = this.userLogin.salesman;

    if (salesman) {
      return this.db.collection(this.dbPath + `/${shopId}/customers`, (ref) =>
        ref
          .where("isDeleted", "==", false)
          .where("isActive", "==", true)
          .where("assignBy", "==", this.userLogin.uid)
          .orderBy("updatedOn", "desc"),
      );
    } else {
      return this.db.collection(this.dbPath + `/${shopId}/customers`, (ref) =>
        ref
          .where("isDeleted", "==", false)
          .where("isActive", "==", true)
          .orderBy("updatedOn", "desc"),
      );
    }
  }

  getCustomers(shopId: string, fromDate?: Date, limit?: number): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/customers`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true);

    if (fromDate) {
      ref = ref.where("updatedOn", ">=", fromDate);
    }
    ref = ref.orderBy("updatedOn", "desc");

    if (limit > 0) {
      ref = ref.limit(limit);
    }

    return ref.get();
  }

  getSyncedCustomers(shopId: string): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/customers`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("isSynced", "==", false)
      .orderBy("updatedOn", "desc");

    return ref.get();
  }

  getListCustomers(shopId: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/customers`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  searchCustomers(shopId: string, uid: string, mobile?: string): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/customers`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("createdBy", "==", uid)
      .where("mobile", "==", mobile)
      .orderBy("updatedOn", "desc");

    return ref.get();
  }

  get(shopId: string, id: string) {
    return this.db
      .collection(this.dbPath + `/${shopId}/customers`)
      .doc(id)
      .valueChanges();
  }

  getCustomer(shopId: string, id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath + `/${shopId}/customers`).doc(id);

    return ref.get();
  }

  create(customer: Customer): any {
    customer.dob = customer.dob ? customer.dob : new Date();
    customer.timestamp = new Date().getTime();
    customer.isSynced = true;
    customer.isActive = true;
    customer.isDeleted = false;
    customer.createdOn = new Date();
    customer.createdBy = this.userLogin.uid;
    customer.updatedOn = new Date();
    customer.updatedBy = this.userLogin.uid;
    customer.author = this.userLogin;
    customer.visibility = this.visibility;

    customer.discountPolicies = customer.discountPolicies
      ? JSON.parse(JSON.stringify(customer.discountPolicies))
      : [];

    if (!customer.assignBy) {
      customer.assignBy = this.userLogin.uid;
      customer.assignee = this.userLogin;
      customer.assignName = this.userLogin.displayName;
    }

    customer.saleQuantity = 0;
    customer.profitAmount = 0;

    customer.initAmount = customer.initAmount ? customer.initAmount : 0;
    customer.saleAmount = customer.initAmount;
    customer.paidAmount = 0;
    customer.debitAmount = customer.saleAmount - customer.paidAmount;

    customer.shopId = customer.shopId ? customer.shopId : this.userLogin.shopId;
    this.collRef = this.db.collection(
      this.dbPath + `/${customer.shopId}/customers`,
    );

    return this.collRef.add({ ...customer }).then((docRef) => {
      customer.id = docRef.id;
      this.updateIndexedDb(customer);
      return customer;
    });
  }

  update(customer: Customer): Promise<void> {
    customer.updatedOn = new Date();
    customer.updatedBy = this.userLogin.uid;

    this.updateIndexedDb(customer);

    this.collRef = this.db.collection(
      this.dbPath + `/${customer.shopId}/customers`,
    );
    return this.collRef.doc(customer.id).update({
      email: customer.email ? customer.email : "",
      mobile: customer.mobile ? customer.mobile : "",
      cardId: customer.cardId ? customer.cardId : "",
      cardIdPlace: customer.cardIdPlace ? customer.cardIdPlace : "",
      cardIdDate: customer.cardIdDate ? customer.cardIdDate : new Date(),
      fullName: customer.fullName ? customer.fullName : "",
      firstName: customer.firstName ? customer.firstName : "",
      lastName: customer.lastName ? customer.lastName : "",
      dob: customer.dob ? customer.dob : new Date(),
      dobYear: customer.dobYear ? customer.dobYear : 2000,
      dobMonth: customer.dobMonth ? customer.dobMonth : 1,
      dobDay: customer.dobDay ? customer.dobDay : 1,
      gender: customer.gender,
      address: customer.address,
      tax: customer.tax ? customer.tax : "",
      description: customer.description,
      coverUrl: customer.coverUrl ? customer.coverUrl : "",
      sourceType: customer.sourceType,
      content: customer.content ? customer.content : "",
      videoUrl: customer.videoUrl ? customer.videoUrl : "",
      imagesUrl: customer.imagesUrl ? customer.imagesUrl : [],
      tags: customer.tags ? customer.tags : [],
      assignBy: customer.assignBy ? customer.assignBy : "",
      assignName: customer.assignName ? customer.assignName : "",
      discountPolicies: customer.discountPolicies
        ? JSON.parse(JSON.stringify(customer.discountPolicies))
        : [],
      updatedOn: customer.updatedOn,
      updatedBy: customer.updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  setCustomerOverview(customer: Customer): Promise<void> {
    customer.isSynced = true;
    customer.updatedOn = new Date();
    customer.updatedBy = this.userLogin.uid;
    customer.timestamp = new Date().getTime();
    customer.profitAmount = customer.profitAmount
      ? parseFloat(customer.profitAmount.toString())
      : 0;
    customer.saleQuantity = customer.saleQuantity
      ? parseFloat(customer.saleQuantity.toString())
      : 0;
    customer.saleAmount = customer.saleAmount
      ? parseFloat(customer.saleAmount.toString())
      : 0;
    customer.paidAmount = customer.paidAmount
      ? parseFloat(customer.paidAmount.toString())
      : 0;
    customer.debitAmount = customer.debitAmount
      ? parseFloat(customer.debitAmount.toString())
      : 0;

    this.updateIndexedDb(customer);

    this.collRef = this.db.collection(
      this.dbPath + `/${customer.shopId}/customers`,
    );
    return this.collRef.doc(customer.id).update({
      profitAmount: customer.profitAmount,
      saleQuantity: customer.saleQuantity,
      saleAmount: customer.saleAmount,
      paidAmount: customer.paidAmount,
      debitAmount: customer.debitAmount,
      totalQuantity: customer.debitAmount,
      totalInvoices: customer.totalInvoices,
      totalAmount: customer.totalAmount,
      isSynced: customer.isSynced,
      syncedOn: customer.updatedOn,
      updatedOn: customer.updatedOn,
      updatedBy: customer.updatedBy,
      timestamp: customer.timestamp,
    });
  }

  setCustomerInitAmount(customer: Customer): Promise<void> {
    customer.updatedOn = new Date();
    customer.updatedBy = this.userLogin.uid;
    customer.timestamp = new Date().getTime();
    customer.initAmount = customer.initAmount
      ? parseFloat(customer.initAmount.toString())
      : 0;

    this.updateIndexedDb(customer);
    console.log(
      "🚀 ~ CustomerService ~ setCustomerInitAmount ~ customer:",
      customer,
    );

    this.collRef = this.db.collection(
      this.dbPath + `/${customer.shopId}/customers`,
    );
    return this.collRef.doc(customer.id).update({
      isInitDept: customer.isInitDept,
      initAmount: customer.initAmount,
      isSynced: customer.isSynced,
      isArchived: customer.isArchived,
      syncedOn: customer.updatedOn,
      updatedOn: customer.updatedOn,
      updatedBy: customer.updatedBy,
      timestamp: customer.timestamp,
    });
  }

  delete(shopId: string, id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    this.deleteIndexedDb(id);

    this.collRef = this.db.collection(this.dbPath + `/${shopId}/customers`);
    return this.collRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  updateIndexedDb(data: Customer) {
    indexedDb.customers.put(data, data.id);

    const index = _.findIndex(this.customers, { id: data.id });
    if (index >= 0) {
      this.customers[index] = data;
    } else {
      this.customers = [data, ...this.customers];
    }

    this._dataChanged.next(data);
  }

  private deleteIndexedDb(id: string) {
    indexedDb.customers.delete(id);

    _.remove(this.customers, (item) => {
      return item.id === id;
    });
  }

  updateStatusChanged(data: Customer) {
    this._dataChanged.next(data);
  }

  calculatorCustomersDebt(shopId: string) {
    localStorage.setItem(
      `${shopId.toLowerCase()}_customers_syncedOn`,
      moment().format("YYYY-MM-DD HH:mm"),
    );

    const callable = this.fns.httpsCallable(
      "api-systemConfig-calculatorCustomersDebt",
    );
    return callable({
      shopId: shopId,
    });
  }

  calculateCustomerStats(
    shopId,
    monthKey,
    customerId,
    fullName = "",
    mobile = "",
    address = "",
    initAmount = 0,
  ) {
    const callable = this.fns.httpsCallable(
      "api-customer-calculateCustomerStats",
    );
    return callable({
      shopId,
      monthKey,
      customerId,
      fullName,
      mobile,
      address,
      initAmount,
    });
  }

  getCustomerStatsSummary(shopId, customerId = "", mode, issueDateYear) {
    const callable = this.fns.httpsCallable(
      "api-customer-getCustomerStatsSummary",
    );

    return callable({
      shopId,
      mode,
      customerId,
      issueDateYear,
      exportAll: true,
    });
  }
}
